import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchEvents,
  createEvent,
  updateEvent,
} from "../services/eventsService";
import { uploadToS3 } from "../../utils/s3Uploader";

// Density Part 4 — Events CMS create/edit. Everything may be saved as a
// draft; publishing is validated server-side (title, image, date, city,
// https RSVP link all required).

const TAGS = [
  { value: "", label: "No tag" },
  { value: "FREE", label: "FREE" },
  { value: "VIP", label: "VIP" },
  { value: "LIMITED_SPOTS", label: "LIMITED SPOTS" },
];

// Common North-American offsets; the offset is stored WITH the date so each
// event displays in its own local time on every phone.
const OFFSETS = [
  { value: "-04:00", label: "Eastern Daylight (EDT, -04:00)" },
  { value: "-05:00", label: "Eastern Standard / Central Daylight (-05:00)" },
  { value: "-06:00", label: "Central Standard / Mountain Daylight (-06:00)" },
  { value: "-07:00", label: "Mountain Standard / Pacific Daylight (-07:00)" },
  { value: "-08:00", label: "Pacific Standard (PST, -08:00)" },
];

const AddEditEventPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);

  const [form, setForm] = useState({
    title: "",
    imageUrl: "",
    date: "",
    time: "19:00",
    offset: "-04:00",
    city: "Ottawa",
    tag: "",
    rsvpUrl: "",
    venue: "",
    address: "",
    priceText: "",
    details: "",
    hostEmails: "",
    hostNote: "",
    geofenceMeters: 150,
    durationHours: 4,
    demoAlwaysOpen: false,
    venueLat: "",
    venueLng: "",
    sortOrder: 0,
    status: "draft",
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!editing) return;
    fetchEvents().then((res) => {
      const event = (res.data?.events || []).find((e) => e._id === id);
      if (!event) return;
      const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/.exec(
        event.eventDateISO || ""
      );
      setForm({
        title: event.title || "",
        imageUrl: event.imageUrl || "",
        date: m ? m[1] : "",
        time: m ? m[2] : "19:00",
        offset: m && m[3] !== "Z" ? m[3] : "-04:00",
        city: event.city || "",
        tag: event.tag || "",
        rsvpUrl: event.rsvpUrl || "",
        venue: event.venue || "",
        address: event.address || "",
        priceText: event.priceText || "",
        details: event.details || "",
        hostEmails: Array.isArray(event.hostEmails) ? event.hostEmails.join(", ") : "",
        hostNote: event.hostNote || "",
        geofenceMeters: event.geofenceMeters ?? 150,
        durationHours: event.durationHours ?? 4,
        demoAlwaysOpen: event.demoAlwaysOpen === true,
        venueLat: event.venueLat ?? "",
        venueLng: event.venueLng ?? "",
        sortOrder: event.sortOrder ?? 0,
        status: event.status || "draft",
      });
    });
  }, [editing, id]);

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const onImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ ok: false, text: "Image must be 2 MB or smaller." });
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setMessage({ ok: false, text: "Use a JPG, PNG, or WebP image." });
      return;
    }
    try {
      setBusy(true);
      const url = await uploadToS3(file, "events");
      setForm((prev) => ({ ...prev, imageUrl: url }));
      setMessage({ ok: true, text: "Image uploaded." });
    } catch {
      setMessage({ ok: false, text: "Image upload failed." });
    } finally {
      setBusy(false);
    }
  };

  const save = async (statusOverride) => {
    const status = statusOverride ?? form.status;
    const eventDateISO =
      form.date && form.time ? `${form.date}T${form.time}:00${form.offset}` : null;
    const payload = {
      title: form.title,
      imageUrl: form.imageUrl || null,
      eventDateISO,
      city: form.city,
      tag: form.tag || null,
      rsvpUrl: form.rsvpUrl || null,
      venue: form.venue || null,
      address: form.address || null,
      priceText: form.priceText || null,
      details: form.details || null,
      hostEmails: form.hostEmails || "",
      hostNote: form.hostNote || null,
      geofenceMeters: form.geofenceMeters,
      durationHours: form.durationHours,
      demoAlwaysOpen: form.demoAlwaysOpen === true,
      sortOrder: form.sortOrder,
      status,
    };
    try {
      setBusy(true);
      const res = editing
        ? await updateEvent(id, payload)
        : await createEvent(payload);
      if (res.data?.success) {
        navigate("/events");
        return;
      }
      setMessage({ ok: false, text: res.data?.message || "Save failed" });
    } catch (err) {
      setMessage({
        ok: false,
        text: err?.response?.data?.message || "Save failed",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">
          {editing ? "Edit Curiouzz Social" : "New Curiouzz Social"}
        </h5>
      </div>
      <div className="card-body" style={{ maxWidth: 640 }}>
        {message && (
          <div
            className={`alert ${message.ok ? "alert-success" : "alert-danger"} py-2`}
          >
            {message.text}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Title (max 80)</label>
          <input
            className="form-control"
            maxLength={80}
            value={form.title}
            onChange={set("title")}
            placeholder="Curiouzz Night: Vault Edition"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Image (16:9 recommended, min 1200px wide, max 2 MB)
          </label>
          <input
            type="file"
            className="form-control"
            accept="image/jpeg,image/png,image/webp"
            onChange={onImagePick}
          />
          {form.imageUrl && (
            <img
              src={form.imageUrl}
              alt="event"
              style={{ maxWidth: 320, marginTop: 8, borderRadius: 8 }}
            />
          )}
        </div>

        <div className="row">
          <div className="col-4 mb-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={form.date}
              onChange={set("date")}
            />
          </div>
          <div className="col-3 mb-3">
            <label className="form-label">Time</label>
            <input
              type="time"
              className="form-control"
              value={form.time}
              onChange={set("time")}
            />
          </div>
          <div className="col-5 mb-3">
            <label className="form-label">Timezone</label>
            <select className="form-select" value={form.offset} onChange={set("offset")}>
              {OFFSETS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label">City</label>
            <input
              className="form-control"
              maxLength={40}
              value={form.city}
              onChange={set("city")}
              placeholder="Ottawa"
            />
          </div>
          <div className="col-3 mb-3">
            <label className="form-label">Tag</label>
            <select className="form-select" value={form.tag} onChange={set("tag")}>
              {TAGS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-3 mb-3">
            <label className="form-label">Sort order</label>
            <input
              type="number"
              className="form-control"
              value={form.sortOrder}
              onChange={set("sortOrder")}
            />
          </div>
        </div>

        {/* Curiouzz Live detail (2026-09-14): what the app's event screen
            shows under the title. All optional. */}
        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label">Venue</label>
            <input className="form-control" maxLength={80} value={form.venue} onChange={set("venue")} placeholder="Beyond the Pale" />
          </div>
          <div className="col-6 mb-3">
            <label className="form-label">Address</label>
            <input className="form-control" maxLength={120} value={form.address} onChange={set("address")} placeholder="2 Beech St, Hintonburg" />
          </div>
        </div>
        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label">What it costs</label>
            <input className="form-control" maxLength={80} value={form.priceText} onChange={set("priceText")} placeholder="$14 at the door, drinks are your own" />
          </div>
          <div className="col-6 mb-3">
            <label className="form-label">What happens (one short paragraph)</label>
            <textarea className="form-control" rows={2} maxLength={400} value={form.details} onChange={set("details")} placeholder="No seating plan. Bring your phone." />
          </div>
        </div>

        {/* Phase two: the room. Coordinates are looked up from the venue
            and address on save; the door code is the event's join code. */}
        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label">Host emails (comma separated, must be members)</label>
            <input className="form-control" value={form.hostEmails} onChange={set("hostEmails")} placeholder="dana@example.com" />
          </div>
          <div className="col-6 mb-3">
            <label className="form-label">How to find the host</label>
            <input className="form-control" maxLength={120} value={form.hostNote} onChange={set("hostNote")} placeholder="Green lanyard, near the bar" />
          </div>
        </div>
        <div className="row">
          <div className="col-3 mb-3">
            <label className="form-label">Geofence (m)</label>
            <input type="number" className="form-control" value={form.geofenceMeters} onChange={set("geofenceMeters")} />
          </div>
          <div className="col-3 mb-3">
            <label className="form-label">Room open (hours after start)</label>
            <input type="number" step="0.5" className="form-control" value={form.durationHours} onChange={set("durationHours")} />
            <div className="form-check mt-12">
              <input
                className="form-check-input"
                type="checkbox"
                id="demoAlwaysOpen"
                checked={form.demoAlwaysOpen === true}
                onChange={(e) => setForm((f) => ({ ...f, demoAlwaysOpen: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="demoAlwaysOpen">
                App Review demo room: open at any hour for test accounts only, and a demo partner joins the
                reviewer's coaster. Use only in the review pocket. Real members never see it.
              </label>
            </div>
          </div>
          <div className="col-6 mb-3">
            <label className="form-label">Venue coordinates (from the address, on save)</label>
            <input className="form-control" readOnly value={form.venueLat !== "" && form.venueLng !== "" ? `${form.venueLat}, ${form.venueLng}` : "not looked up yet"} />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label">RSVP link (https, e.g. Eventbrite)</label>
          <input
            className="form-control"
            value={form.rsvpUrl}
            onChange={set("rsvpUrl")}
            placeholder="https://www.eventbrite.com/e/…"
          />
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            disabled={busy}
            onClick={() => save("draft")}
          >
            Save draft
          </button>
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() => save("published")}
          >
            {busy ? "Saving…" : "Publish"}
          </button>
          <button className="btn btn-link" onClick={() => navigate("/events")}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditEventPage;
